import React, {useEffect, useMemo, useState} from "react";
import {Badge, Button, Checkbox, Collapse, DatePicker, Dropdown, Empty, Select, Space, Table, Tag, Tooltip} from "antd";
import type {ColumnsType, TableProps as AntTableProps} from "antd/es/table";
import {DownloadOutlined, FilterOutlined, SettingOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import {IPageInfo} from "../../redux/types";
import {COLORS, SearchField} from "../../designSystem";
import {withSorters} from "./sorting";

const {RangePicker} = DatePicker;

/* ── Types ─────────────────────────────────────────────────────────────────── */

export interface DataTableFilter {
  key: string;
  label: string;
  value: any;
  onChange: (value: any) => void;
  options: Array<{label: string; value: any}>;
  /** Width of the control. Defaults to 180. */
  width?: number;
}

export interface DataTableGrouping {
  /** The dimension currently grouped by, or undefined for a flat table. */
  value?: string;
  onChange: (value: string | undefined) => void;
  options: Array<{
    key: string;
    label: string;
    /** The bucket a row belongs to. Falsy results collapse into "Not set". */
    get: (row: any) => any;
  }>;
}

export interface DataTableProps {
  columns: ColumnsType<any>;
  data: any[] | null;
  pageInfo: IPageInfo;
  handlePageChange?: (pageNumber: number) => void;
  loading?: boolean;
  expandable?: AntTableProps<any>["expandable"];
  /** Hide paging for endpoints that return a whole list rather than a page of one. */
  paginated?: boolean;

  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Remembers successful searches under this key, for type-ahead. */
  historyKey?: string;
  /** Extra type-ahead entries, e.g. values already on screen. */
  searchSuggestions?: string[];

  /** Date range, as [start, end] dayjs values. Presets are supplied by the toolbar. */
  dateRange?: any;
  onDateRangeChange?: (value: any) => void;
  dateLabel?: string;

  filters?: DataTableFilter[];
  grouping?: DataTableGrouping;

  /** Numeric columns to total, per group and overall. */
  totals?: Array<{label: string; dataIndex: string; format?: (value: number) => string}>;

  exportFileName?: string;
  /** Extra controls rendered at the right of the toolbar (page actions). */
  actions?: React.ReactNode;
  emptyText?: string;
}

/* ── Helpers ───────────────────────────────────────────────────────────────── */

const columnKey = (column: any, index: number) =>
  String(column.key ?? column.dataIndex ?? `column-${index}`);

const columnTitle = (column: any) =>
  typeof column.title === "string" ? column.title : String(column.key ?? column.dataIndex ?? "");

/**
 * Plain text for a cell, for CSV. Falls back to the raw field: a `render` returning JSX
 * cannot be serialised, and a column that needs something else can set `exportValue`.
 */
const exportCell = (column: any, row: any) => {
  if (typeof column.exportValue === "function") return column.exportValue(row);
  const raw = column.dataIndex ? row?.[column.dataIndex] : undefined;
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "object") return "";
  return String(raw);
};

const toCsv = (columns: any[], rows: any[]) => {
  const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
  const header = columns.map((column) => escape(columnTitle(column))).join(",");
  const body = rows.map((row) => columns.map((column) => escape(exportCell(column, row))).join(","));
  return [header, ...body].join("\n");
};

const sumBy = (rows: any[], dataIndex: string) =>
  rows.reduce((total, row) => total + (Number(row?.[dataIndex]) || 0), 0);

/* ── Component ─────────────────────────────────────────────────────────────── */

/**
 * The list surface used across the admin app: filtering and date scoping above, sorting and
 * grouping on the data itself, and an export of exactly what is on screen.
 *
 * Search, date and dimension filters are *server-side* — they are reported upward and the page
 * refetches. Sorting and grouping are client-side and therefore apply to the loaded page only,
 * which the toolbar says out loud rather than letting a reader assume the whole result set was
 * ordered.
 */
export function DataTable({
  columns,
  data,
  pageInfo,
  handlePageChange,
  loading = false,
  expandable,
  paginated = true,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search",
  historyKey,
  searchSuggestions,
  dateRange,
  onDateRangeChange,
  dateLabel = "Date range",
  filters = [],
  grouping,
  totals = [],
  exportFileName = "export",
  actions,
  emptyText = "Nothing matches these filters.",
}: DataTableProps) {
  const rows = useMemo(() => data || [], [data]);

  const allKeys = useMemo(() => columns.map((column, index) => columnKey(column, index)), [columns]);
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  // Columns can change with the page; drop hidden keys that no longer exist so a stale key
  // never silently hides a different column that happens to reuse the name.
  useEffect(() => {
    setHiddenKeys((current) => current.filter((key) => allKeys.includes(key)));
  }, [allKeys]);

  /**
   * Visible columns, each given a sorter unless it already has one or opts out with
   * `sortAs: false`. Sorting every page by hand would mean annotating roughly two hundred
   * columns; deriving it means a new column is sortable the day it is added.
   */
  /**
   * Visible columns, each given a derived sorter by the shared helper so this table and the
   * plain GenericTable order data by exactly the same rules.
   */
  const visibleColumns = useMemo(
    () => withSorters(
      columns.filter((column, index) => !hiddenKeys.includes(columnKey(column, index))) as any[],
      rows,
    ) as any[],
    [columns, hiddenKeys, rows],
  );

  const activeGroup = grouping?.options.find((option) => option.key === grouping?.value);

  const groups = useMemo(() => {
    if (!activeGroup) return [];
    const buckets = new Map<string, any[]>();
    rows.forEach((row) => {
      const raw = activeGroup.get(row);
      const label = raw === null || raw === undefined || raw === "" ? "Not set" : String(raw);
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label)!.push(row);
    });
    return Array.from(buckets.entries())
      .map(([label, groupRows]) => ({label, rows: groupRows}))
      .sort((a, b) => b.rows.length - a.rows.length);
  }, [rows, activeGroup]);

  const activeFilters = [
    ...(searchValue ? [{key: "search", label: `Search: ${searchValue}`, clear: () => onSearchChange?.("")}] : []),
    ...(dateRange?.[0] && dateRange?.[1]
      ? [{
        key: "date",
        label: `${dateRange[0].format?.("DD MMM YYYY")} – ${dateRange[1].format?.("DD MMM YYYY")}`,
        clear: () => onDateRangeChange?.(null),
      }]
      : []),
    ...filters
      .filter((filter) => filter.value !== undefined && filter.value !== null && filter.value !== "")
      .map((filter) => ({
        key: filter.key,
        label: `${filter.label}: ${filter.options.find((option) => option.value === filter.value)?.label ?? filter.value}`,
        clear: () => filter.onChange(undefined),
      })),
    ...(activeGroup ? [{key: "group", label: `Grouped by ${activeGroup.label}`, clear: () => grouping?.onChange(undefined)}] : []),
  ];

  const clearAll = () => activeFilters.forEach((filter) => filter.clear());

  const downloadCsv = () => {
    const csv = toCsv(visibleColumns as any[], rows);
    const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${exportFileName}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const tableFor = (tableRows: any[], withPaging: boolean) => (
    <Table
      rowKey="key"
      size="middle"
      loading={loading}
      scroll={{x: "100%"}}
      columns={visibleColumns}
      dataSource={tableRows}
      expandable={expandable}
      locale={{emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText}/>}}
      pagination={withPaging && paginated ? {
        total: pageInfo?.totalItems || 0,
        current: pageInfo?.pageNumber || 1,
        pageSize: pageInfo?.pageSize || 10,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      } : false}
      onChange={(pagination) => {
        if (withPaging && pagination?.current !== pageInfo?.pageNumber && handlePageChange) {
          handlePageChange(pagination.current as number);
        }
      }}
      summary={totals.length ? () => (
        <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={visibleColumns.length}>
              <Space size={16} wrap>
                {totals.map((total) => (
                  <span key={total.dataIndex} className="text-xs" style={{color: COLORS.textMuted}}>
                    {total.label}:{" "}
                    <span className="font-mono font-semibold" style={{color: COLORS.text}}>
                      {(total.format || String)(sumBy(tableRows, total.dataIndex))}
                    </span>
                  </span>
                ))}
              </Space>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      ) : undefined}
    />
  );

  return (
    <div>
      {/* Toolbar: scoping on the left, view controls on the right. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {onSearchChange && (
            <SearchField
              className="w-full sm:w-72"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              loading={loading}
              historyKey={historyKey}
              suggestions={searchSuggestions}
            />
          )}

          {onDateRangeChange && (
            <RangePicker
              value={dateRange}
              onChange={onDateRangeChange}
              allowClear
              placeholder={[dateLabel, ""]}
              style={{height: 44}}
              ranges={{
                Today: [startOfToday(), endOfToday()],
                "Last 7 days": [daysAgo(6), endOfToday()],
                "Last 30 days": [daysAgo(29), endOfToday()],
                "This month": [startOfMonth(0), endOfToday()],
                "Last month": [startOfMonth(1), endOfMonth(1)],
              } as any}
            />
          )}

          {filters.map((filter) => (
            <Select
              key={filter.key}
              allowClear
              placeholder={filter.label}
              value={filter.value ?? undefined}
              onChange={filter.onChange}
              options={filter.options}
              style={{width: filter.width || 180, height: 44}}
            />
          ))}
        </div>

        <Space wrap>
          {grouping && (
            <Select
              allowClear
              placeholder="Group by"
              value={grouping.value ?? undefined}
              onChange={grouping.onChange}
              style={{width: 180, height: 44}}
              options={grouping.options.map((option) => ({label: option.label, value: option.key}))}
            />
          )}

          <Dropdown
            trigger={["click"]}
            popupRender={() => (
              <div className="rounded-xl bg-white p-3 shadow-lg" style={{border: `1px solid ${COLORS.cardBorder}`}}>
                <p className="mb-2 text-xs font-semibold" style={{color: COLORS.textMuted}}>Columns</p>
                <Space direction="vertical">
                  {columns.map((column, index) => {
                    const key = columnKey(column, index);
                    return (
                      <Checkbox
                        key={key}
                        checked={!hiddenKeys.includes(key)}
                        onChange={(event) =>
                          setHiddenKeys((current) =>
                            event.target.checked ? current.filter((item) => item !== key) : [...current, key])
                        }
                      >
                        {columnTitle(column)}
                      </Checkbox>
                    );
                  })}
                </Space>
              </div>
            )}
          >
            <Button icon={<SettingOutlined/>} style={{height: 44}}>
              Columns{hiddenKeys.length ? ` (${allKeys.length - hiddenKeys.length}/${allKeys.length})` : ""}
            </Button>
          </Dropdown>

          <Tooltip title="Download the rows currently loaded, with the visible columns">
            <Button icon={<DownloadOutlined/>} onClick={downloadCsv} disabled={!rows.length} style={{height: 44}}>
              Export
            </Button>
          </Tooltip>

          {actions}
        </Space>
      </div>

      {/* Active scope, stated plainly and individually removable. */}
      {activeFilters.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <FilterOutlined style={{color: COLORS.textMuted}}/>
          {activeFilters.map((filter) => (
            <Tag key={filter.key} closable onClose={filter.clear} color="blue">{filter.label}</Tag>
          ))}
          <Button type="link" size="small" onClick={clearAll}>Clear all</Button>
        </div>
      )}

      {activeGroup ? (
        <div className="mt-4">
          {/*
            Sorting and grouping run over the rows already loaded, so they describe this page
            and not the whole result set. Saying so beats letting a reader assume otherwise.
          */}
          <p className="mb-2 text-xs" style={{color: COLORS.textMuted}}>
            Grouping applies to the {rows.length} rows on this page.
          </p>
          <Collapse
            defaultActiveKey={groups.slice(0, 3).map((group) => group.label)}
            items={groups.map((group) => ({
              key: group.label,
              label: (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold" style={{color: COLORS.text}}>{group.label}</span>
                  <Badge
                    count={group.rows.length}
                    style={{background: COLORS.brandTint, color: COLORS.brand}}
                    overflowCount={9999}
                  />
                  {totals.map((total) => (
                    <span key={total.dataIndex} className="text-xs font-mono" style={{color: COLORS.textMuted}}>
                      {(total.format || String)(sumBy(group.rows, total.dataIndex))}
                    </span>
                  ))}
                </span>
              ),
              children: tableFor(group.rows, false),
            }))}
          />
        </div>
      ) : (
        <div className="mt-4">
          {tableFor(rows, true)}
          {/*
            The admin API takes no sort parameter, so ordering happens over the rows already
            fetched. With more than one page that is a meaningfully different thing from
            sorting the result set, and the table should say so rather than imply otherwise.
          */}
          {paginated && (pageInfo?.totalPages || 0) > 1 && (
            <p className="mt-2 mb-0 text-xs" style={{color: COLORS.textMuted}}>
              Sorting orders the {rows.length} rows on this page. Narrow the filters or the date
              range to sort across the whole result set.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Date-range presets ────────────────────────────────────────────────────── */
// antd v6 picks dates with dayjs, so these presets speak the same type the RangePicker does.

const startOfToday = () => dayjs().startOf("day");
const endOfToday = () => dayjs().endOf("day");
const daysAgo = (days: number) => dayjs().subtract(days, "day").startOf("day");
const startOfMonth = (monthsAgo: number) => dayjs().subtract(monthsAgo, "month").startOf("month");
const endOfMonth = (monthsAgo: number) => dayjs().subtract(monthsAgo, "month").endOf("month");

export default DataTable;
